import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck } from 'lucide-react';
import LoginRedirect from '../../components/LoginRedirect';
import botspotLogo from '../../assets/botspot_logo_horizontal.png';
import alpacaLogo from '../../assets/broker-logos/alpaca.png';
import coinbaseLogo from '../../assets/broker-logos/coinbase.png';
import krakenLogo from '../../assets/broker-logos/kraken.png';
import schwabLogo from '../../assets/broker-logos/schwab.png';
import tradierLogo from '../../assets/broker-logos/tradier.png';
import './AuthFirstSalesPage.css';
import TacticalLedger from './authFirstDesigns/TacticalLedger';

type AuthFirstSalesPageProps = {
  analyticsContext?: Record<string, unknown>;
  entryMode?: 'discovery' | 'checkout' | 'login' | 'signup';
  onAuthenticated?: () => void;
  presentation?: 'page' | 'overlay';
  returnTo: string;
};

const brokers = [
  { name: 'Alpaca', logo: alpacaLogo },
  { name: 'Charles Schwab', logo: schwabLogo },
  { name: 'Tradier', logo: tradierLogo },
  { name: 'Coinbase', logo: coinbaseLogo },
  { name: 'Kraken', logo: krakenLogo },
];

const desktopProductSubheading = (
  "Chat with BotSpot's AI Agent to build and refine your strategy, run historical backtests, and connect a broker when you're ready."
);

const mobileProductSubheading = (
  'Chat with AI to build and backtest your trading strategy.'
);

const getReducedMotionPreference = (): boolean => (
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches)
);

const getMobileLayoutPreference = (): boolean => (
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  Boolean(window.matchMedia('(max-width: 960px)')?.matches)
);

const AuthFirstSalesPage = ({
  analyticsContext,
  entryMode = 'discovery',
  onAuthenticated,
  presentation = 'page',
  returnTo,
}: AuthFirstSalesPageProps): React.ReactElement => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getReducedMotionPreference);
  const [isMobileLayout, setIsMobileLayout] = useState(getMobileLayoutPreference);
  const productSubheading = isMobileLayout
    ? mobileProductSubheading
    : desktopProductSubheading;
  const shouldAnimateSubheading = !prefersReducedMotion && !isMobileLayout;
  const [typedSubheading, setTypedSubheading] = useState(() => (
    getReducedMotionPreference()
      ? (getMobileLayoutPreference() ? mobileProductSubheading : desktopProductSubheading)
      : ''
  ));
  const [subheadingTypingComplete, setSubheadingTypingComplete] = useState(
    getReducedMotionPreference,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!motionPreference) {
      return undefined;
    }
    const updateMotionPreference = () => setPrefersReducedMotion(motionPreference.matches);
    updateMotionPreference();
    if (motionPreference.addEventListener) {
      motionPreference.addEventListener('change', updateMotionPreference);
    } else {
      motionPreference.addListener?.(updateMotionPreference);
    }

    return () => {
      if (motionPreference.removeEventListener) {
        motionPreference.removeEventListener('change', updateMotionPreference);
      } else {
        motionPreference.removeListener?.(updateMotionPreference);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mobileLayout = window.matchMedia('(max-width: 960px)');
    if (!mobileLayout) {
      return undefined;
    }
    const updateMobileLayout = () => setIsMobileLayout(mobileLayout.matches);
    updateMobileLayout();
    if (mobileLayout.addEventListener) {
      mobileLayout.addEventListener('change', updateMobileLayout);
    } else {
      mobileLayout.addListener?.(updateMobileLayout);
    }

    return () => {
      if (mobileLayout.removeEventListener) {
        mobileLayout.removeEventListener('change', updateMobileLayout);
      } else {
        mobileLayout.removeListener?.(updateMobileLayout);
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldAnimateSubheading) {
      setTypedSubheading(productSubheading);
      setSubheadingTypingComplete(true);
      return undefined;
    }

    let characterIndex = 0;
    let timeoutId: number;
    setTypedSubheading('');
    setSubheadingTypingComplete(false);

    const typeNextCharacter = () => {
      characterIndex += 1;
      setTypedSubheading(productSubheading.slice(0, characterIndex));

      if (characterIndex >= productSubheading.length) {
        setSubheadingTypingComplete(true);
        return;
      }

      const typedCharacter = productSubheading[characterIndex - 1];
      const delay = /[,.]/.test(typedCharacter) ? 110 : typedCharacter === ' ' ? 12 : 22;
      timeoutId = window.setTimeout(typeNextCharacter, delay);
    };

    timeoutId = window.setTimeout(typeNextCharacter, 260);
    return () => window.clearTimeout(timeoutId);
  }, [productSubheading, shouldAnimateSubheading]);

  const isCheckoutEntry = entryMode === 'checkout';
  const isLoginEntry = entryMode === 'login';
  const isSignupEntry = entryMode === 'signup';
  const navAction = isCheckoutEntry ? 'Continue' : isLoginEntry ? 'Sign in' : 'Create account';
  const authHeading = isCheckoutEntry
    ? 'Create or sign in to continue'
    : isLoginEntry
      ? 'Sign in to BotSpot'
      : 'Create your BotSpot account';
  const authDescription = isCheckoutEntry
    ? 'Use Google or email. Your plan is saved.'
    : isLoginEntry
      ? 'Use Google or email.'
      : 'Use Google or email.';
  const authSource = isCheckoutEntry
    ? 'checkout_auth_first'
    : isLoginEntry
      ? 'login_auth_first'
      : isSignupEntry
        ? 'signup_auth_first'
        : 'sales_auth_first';
  const assuranceCopy = isCheckoutEntry
    ? 'Nothing is charged on this page.'
    : isSignupEntry
      ? 'Creating an account does not connect a broker or place a trade.'
      : 'Signing in does not connect a broker or place a trade.';
  const isOverlay = presentation === 'overlay';
  const subheading = (
    <p aria-label={productSubheading} className="auth-first-sales__subheading">
      {shouldAnimateSubheading ? (
        <>
          <span aria-hidden="true" className="auth-first-sales__subheading-reserve">
            {productSubheading}
          </span>
          <span aria-hidden="true" className="auth-first-sales__subheading-typed">
            {typedSubheading}
            {!subheadingTypingComplete && (
              <span className="auth-first-sales__typing-cursor" />
            )}
          </span>
        </>
      ) : productSubheading}
    </p>
  );

  const authContent = (
    <aside
      aria-label={isCheckoutEntry
        ? 'Create or sign in to continue'
        : isLoginEntry
          ? 'Sign in to BotSpot'
          : 'Create your BotSpot account'}
      className="auth-first-sales__conversion"
      id="auth-card-section"
    >
      <div className="auth-first-sales__auth-card">
        <LoginRedirect
          analyticsContext={analyticsContext}
          descriptionOverride={authDescription}
          headingOverride={authHeading}
          onAuthenticated={onAuthenticated}
          returnToOverride={returnTo}
          sourceOverride={authSource}
        />
      </div>

      <div className="auth-first-sales__assurance">
        <ShieldCheck aria-hidden="true" focusable="false" />
        <p>{assuranceCopy}</p>
      </div>

      {isCheckoutEntry ? (
        <div className="auth-first-sales__checkout-context">
          <a className="auth-first-sales__pricing-link" href="/pricing">
            Return to pricing
          </a>
        </div>
      ) : null}

      <div className="auth-first-sales__brokers">
        <span className="auth-first-sales__brokers-label">Supported brokers</span>
        <ul aria-label="Supported brokers">
          {brokers.map(({ name, logo }) => (
            <li key={name}>
              <img alt="" aria-hidden="true" height={20} src={logo} width={72} />
              <span>{name}</span>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );

  return (
    <div
      className={`auth-first-sales auth-page bsui-scope${isOverlay ? ' auth-first-sales--overlay' : ''}`}
      data-bs-theme="dark"
      data-design="tactical-ledger"
      data-entry-mode={entryMode}
      data-presentation={presentation}
    >
      <Helmet>
        <title>
          {isCheckoutEntry
            ? 'Create Your BotSpot Account | Continue to Checkout'
            : isLoginEntry
              ? 'Sign In to BotSpot | Review the Product'
              : 'BotSpot | Build and Backtest a Trading Bot with AI'}
        </title>
        <meta
          name="description"
          content={isCheckoutEntry
            ? 'Create or sign in to BotSpot, then return to checkout with your plan saved.'
            : "Build a trading strategy with BotSpot's AI Agent, backtest it on historical data, and connect a broker when you're ready."}
        />
        {entryMode === 'discovery' && (
          <link rel="canonical" href="https://botspot.trade/sales" />
        )}
        <meta name="theme-color" content="#080b18" />
      </Helmet>

      {!isOverlay && (
        <header className="auth-first-nav">
          <div className="auth-first-nav__inner">
            <a aria-label="BotSpot home" className="auth-first-nav__logo" href="/">
              <img alt="BotSpot by LumiWealth" height={429} src={botspotLogo} width={1385} />
            </a>
            <a className="auth-first-nav__action" href="#auth-card-section">
              {navAction}
            </a>
          </div>
        </header>
      )}

      <main
        aria-labelledby={isMobileLayout ? undefined : 'auth-first-title'}
        className="auth-first-sales__container"
      >
        <TacticalLedger
          authContent={authContent}
          subheading={subheading}
          titleId="auth-first-title"
        />
      </main>

      <footer className="auth-first-sales__footer">
        <p>Trading involves risk. Backtests and paper trading do not guarantee future results.</p>
      </footer>

    </div>
  );
};

export default AuthFirstSalesPage;
